// Adaptation frontend de parseCJVariantOptions utilisé par editProduct côté serveur.
// CJ ne fournit pas toujours optionValues/attributes : la couleur et la taille
// sont alors encodées dans le nom ou le SKU (ex. Grey9668-3XL).
const COLORS = [
  ["Off White", "#FAF9F6", ["off white", "offwhite"]],
  ["Light Gray", "#D3D3D3", ["light gray", "light grey", "lightgray", "lightgrey"]],
  ["Dark Gray", "#A9A9A9", ["dark gray", "dark grey", "darkgray", "darkgrey"]],
  ["Navy Blue", "#000080", ["navy blue", "navyblue"]],
  ["Royal Blue", "#4169E1", ["royal blue", "royalblue"]],
  ["Sky Blue", "#87CEEB", ["sky blue", "skyblue"]],
  ["Light Blue", "#ADD8E6", ["light blue", "lightblue"]],
  ["Dark Blue", "#00008B", ["dark blue", "darkblue"]],
  ["Light Green", "#90EE90", ["light green", "lightgreen"]],
  ["Dark Green", "#006400", ["dark green", "darkgreen"]],
  ["Forest Green", "#228B22", ["forest green", "forestgreen"]],
  ["Mint Green", "#98FF98", ["mint green", "mintgreen"]],
  ["Rose Gold", "#B76E79", ["rose gold", "rosegold"]],
  ["Black", "#000000", ["black", "noir"]], ["White", "#FFFFFF", ["white"]],
  ["Gray", "#808080", ["gray", "grey"]], ["Silver", "#C0C0C0", ["silver"]],
  ["Charcoal", "#36454F", ["charcoal"]], ["Red", "#FF0000", ["red"]],
  ["Burgundy", "#800020", ["burgundy"]], ["Maroon", "#800000", ["maroon"]],
  ["Wine", "#722F37", ["wine"]], ["Pink", "#FFC0CB", ["pink"]],
  ["Purple", "#800080", ["purple"]], ["Violet", "#8F00FF", ["violet"]],
  ["Blue", "#0000FF", ["blue"]], ["Navy", "#000080", ["navy"]],
  ["Teal", "#008080", ["teal"]], ["Turquoise", "#40E0D0", ["turquoise"]],
  ["Green", "#008000", ["green"]], ["Olive", "#808000", ["olive"]],
  ["Yellow", "#FFFF00", ["yellow"]], ["Gold", "#FFD700", ["gold", "golden"]],
  ["Orange", "#FFA500", ["orange"]], ["Brown", "#A52A2A", ["brown"]],
  ["Tan", "#D2B48C", ["tan"]], ["Beige", "#F5F5DC", ["beige"]],
  ["Cream", "#FFFDD0", ["cream"]], ["Ivory", "#FFFFF0", ["ivory"]],
  ["Khaki", "#F0E68C", ["khaki"]], ["Multicolor", null, ["multicolor", "multi color"]],
].map(([label, hex, aliases]) => ({ label, hex, aliases }));

const SIZE_LABELS = {
  xxs: "XXS", xs: "XS", s: "S", small: "Small", m: "M", medium: "Medium",
  l: "L", large: "Large", xl: "XL", xxl: "XXL", "2xl": "2XL", "3xl": "3XL",
  "4xl": "4XL", "5xl": "5XL", "6xl": "6XL",
};

const compact = (value) => String(value || "").normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");

const normalizeKey = (key) => {
  const value = compact(key);
  if (["color", "colour", "couleur", "variantcolor"].includes(value)) return "Color";
  if (["size", "taille", "variantsize"].includes(value)) return "Size";
  return String(key || "").trim();
};

const optionObject = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try { return optionObject(JSON.parse(value)); } catch (_error) { return {}; }
  }
  if (Array.isArray(value)) return value.reduce((result, item) => {
    const key = item?.name || item?.key || item?.label;
    const optionValue = item?.value || item?.optionValue;
    if (key && optionValue !== undefined) result[normalizeKey(key)] = String(optionValue).trim();
    return result;
  }, {});
  if (typeof value !== "object") return {};
  return Object.entries(value).reduce((result, [key, optionValue]) => {
    const resolved = Array.isArray(optionValue) ? optionValue[0] : optionValue;
    if (resolved !== undefined && resolved !== null && String(resolved).trim()) {
      result[normalizeKey(key)] = String(resolved).trim();
    }
    return result;
  }, {});
};

const structuredOptions = (variant) => {
  for (const source of [variant?.optionValues, variant?.attributes, variant?.options]) {
    const options = optionObject(source);
    if (Object.keys(options).length) return options;
  }
  return {};
};

const stripProductTitle = (productTitle, variantName) => {
  const title = String(productTitle || "").trim();
  const name = String(variantName || "").trim();
  return title && name.toLowerCase().startsWith(title.toLowerCase()) ? name.slice(title.length).trim() : name;
};

const findColor = (text) => {
  const normalized = compact(text);
  const candidates = COLORS.flatMap((color) => color.aliases.map((alias) => ({ color, alias: compact(alias) })))
    .sort((a, b) => b.alias.length - a.alias.length);
  const found = candidates.find(({ alias }) => {
    const index = normalized.indexOf(alias);
    if (index < 0) return false;
    const before = normalized.slice(0, index);
    const after = normalized.slice(index + alias.length);
    return (!before || /\d$/.test(before)) && (!after || /^\d/.test(after) || /^(?:x{0,2}[sml]|[2-6]xl)/.test(after));
  });
  return found?.color || null;
};

const findSize = (text) => {
  const original = String(text || "").toLowerCase();
  const numericSize = original.match(/(?:^|[^a-z0-9])([2-6]\s*x\s*l)(?=$|[^a-z0-9])/i);
  if (numericSize) return numericSize[1].replace(/\s+/g, "").toUpperCase();
  const spaced = original.replace(/([a-z])(\d)/g, "$1 $2")
    .replace(/(\d)([a-z])/g, "$1 $2").replace(/[^a-z0-9]+/g, " ").trim();
  const tokens = spaced.split(/\s+/);
  const keys = Object.keys(SIZE_LABELS).sort((a, b) => b.length - a.length);
  const key = [...tokens].reverse().find((token) => keys.includes(token))
    || keys.find((candidate) => compact(text).endsWith(candidate));
  return key ? SIZE_LABELS[key] : "";
};

const parseProductDropshippingVariantFilters = (productTitle, variant) => {
  const options = structuredOptions(variant);
  if (Object.keys(options).length) return options;
  const nameText = stripProductTitle(productTitle, variant?.name || variant?.title);
  const color = findColor(nameText) || findColor(variant?.sku);
  const size = findSize(nameText) || findSize(variant?.sku);
  const filters = {};
  if (color) filters.Color = color.label;
  if (size) filters.Size = size;
  return filters;
};

const getProductDropshippingColor = (value) => {
  const normalized = compact(value);
  const definition = COLORS.find((color) => compact(color.label) === normalized
    || color.aliases.some((alias) => compact(alias) === normalized));
  return definition?.hex || String(value || "transparent").replace(/\d+/g, "").trim();
};

module.exports = {
  parseProductDropshippingVariantFilters,
  getProductDropshippingColor,
};
